import { useCallback, useEffect, useRef } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { ToastStatus } from '~/hooks/useToast';

import { useCommunityFetcher } from './useCommunityFetcher';
import { communityMemberPostsKeys } from './useCommunityMemberPosts';
import { useCommunityPostOptimistic } from './useCommunityPostOptimistic';
import { communityPostsKeys } from './useCommunityPosts';

export const PROFILE_PIN_POST_ACTION = '/pin_community_post_to_profile';
export const PROFILE_PIN_FETCHER_KEY = 'pin-community-post-to-profile';

const QUERY_KEY = [...communityPostsKeys.all];

/**
 * Surfaces the toast and refreshes the caches after a profile pin submission settles.
 *
 * Mount once at the community layout: the action is offered on every surface that renders a
 * post action menu, not just the feed.
 */
export const useProfilePinFetcher = () => {
  const queryClient = useQueryClient();
  const { applyOptimisticPatch, clearOptimisticPatch } = useCommunityPostOptimistic();
  const prevStateRef = useRef('idle');
  const patchedPostIdRef = useRef<string | null>(null);

  const onSubmissionComplete = useCallback(() => {
    const postId = patchedPostIdRef.current;
    if (!postId) return;

    void queryClient.invalidateQueries({ queryKey: communityMemberPostsKeys.all }).finally(() => {
      if (patchedPostIdRef.current !== postId) return;
      clearOptimisticPatch(postId);
      patchedPostIdRef.current = null;
    });
  }, [queryClient, clearOptimisticPatch]);

  const { fetcher } = useCommunityFetcher(PROFILE_PIN_FETCHER_KEY, {
    queryKey: QUERY_KEY,
    onSubmissionComplete,
  });

  useEffect(() => {
    const prevState = prevStateRef.current;
    prevStateRef.current = fetcher.state;

    if (prevState === 'idle' && fetcher.state !== 'idle' && fetcher.formData) {
      const postId = fetcher.formData.get('post_id');
      const actionType = fetcher.formData.get('action_type');
      if (typeof postId === 'string') {
        patchedPostIdRef.current = postId;
        applyOptimisticPatch(postId, { is_profile_pinned: actionType === 'pin' });
      }
    }
  }, [fetcher.state, fetcher.formData, applyOptimisticPatch]);

  useEffect(() => {
    if (fetcher.state !== 'idle' || !fetcher.data || !patchedPostIdRef.current) return;
    if (fetcher.data.toast?.status !== ToastStatus.ERROR) return;

    const postId = patchedPostIdRef.current;
    patchedPostIdRef.current = null;
    clearOptimisticPatch(postId);
  }, [fetcher.state, fetcher.data, clearOptimisticPatch]);

  return { fetcher };
};
